/**
 * 检查项记录管理入口（路由 /myse/check-records，旧 /myse/orders 保留兼容）
 *
 * 仅做 list ↔ detail 视图切换 — view + checkId 由 URL `?check_id=xxx` 派生,
 * URL 是单一数据源(刷新留在详情 / 浏览器返回回列表 / 外部链接直达详情都靠它).
 * 数据状态全部下沉到子组件 hooks（不使用 Redux slice）。
 */
import React, { useCallback, useMemo } from 'react'
import { Layout } from 'antd'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import List from './records/List'
import Detail from './records/Detail'
import './index.scss'

const { Content } = Layout

const CheckRecordsPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const reduxState = useAppSelector((state) => state)
  const navigate = useNavigate()
  const location = useLocation()

  // view + currentCheckId 全部从 URL 派生 — URL 是单一数据源,
  // 这样刷新页面 / 浏览器前进后退 / 外部链接打开都自动同步,无需 useEffect 维护
  // 注:cid 必须保持 string,雪花 ID 17 位以上 Number() 会精度丢失
  const { view, currentCheckId } = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const cid = params.get('check_id') || ''
    if (cid && /^\d+$/.test(cid)) {
      return { view: 'detail' as const, currentCheckId: cid as string | number }
    }
    return { view: 'list' as const, currentCheckId: 0 as string | number }
  }, [location.search])

  const handleOpenDetail = useCallback(
    (checkId: number | string) => {
      // 通过 react-router navigate 让 URL 变成 ?check_id=xxx,
      // 上面的 useMemo 依赖 location.search,会自动重算 view → 进 detail
      navigate(
        `${location.pathname}?check_id=${encodeURIComponent(String(checkId))}`
      )
    },
    [navigate, location.pathname]
  )

  const handleBackToList = useCallback(() => {
    // 把 URL 清回 pathname(去掉 query),useMemo 重算 view → 回 list
    navigate(location.pathname)
  }, [navigate, location.pathname])

  return (
    <Layout className="check-records-page">
      <Content style={{ padding: 24 }}>
        {/* List 常驻不卸载,仅切换可见性,保留筛选/分页等本地 state */}
        <div style={{ display: view === 'list' ? 'block' : 'none' }}>
          <List
            dispatch={dispatch}
            state={reduxState}
            onOpenDetail={handleOpenDetail}
          />
        </div>
        {view === 'detail' && (
          <Detail
            checkId={currentCheckId}
            dispatch={dispatch}
            state={reduxState}
            onBack={handleBackToList}
          />
        )}
      </Content>
    </Layout>
  )
}

export default CheckRecordsPage
